import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, FileText, Download, Eye, X, Maximize2 } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Certifications() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/certifications`)
      .then((res) => setCerts(res.data || []))
      .catch(() => setCerts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-certifications">
      <Seo
        title="Certifications — ISO 9001:2015 Certified"
        description="NEXORIQ is certified with ISO 9001:2015 Quality Management System. View and download our current certifications and quality credentials."
        path="/certifications"
        structuredData={ORG_LD}
      />

      {/* Hero */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24" data-testid="cert-hero">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">
          Certifications · Quality credentials
        </div>
        <RevealLines
          as="h1"
          lines={[<>Certified.</>, <>Audited. <span className="text-nx-copper">Accountable.</span></>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <p className="mt-8 max-w-2xl text-nx-bg/75 leading-relaxed text-lg">
          Quality is not a claim at NEXORIQ — it is a certified, audited system. Every brief we
          handle runs through documented processes verified by independent certification bodies.
        </p>
      </section>

      {/* ISO 9001:2015 highlight */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-b border-nx-navy/20" data-testid="cert-iso-highlight">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-1">
            <BadgeCheck size={44} strokeWidth={1.6} className="text-nx-copper" />
          </div>
          <div className="lg:col-span-11">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-3xl md:text-5xl text-nx-navy">
              ISO 9001:2015 — Quality Management System
            </h2>
            <p className="mt-6 max-w-3xl text-nx-navy2 leading-relaxed text-lg">
              NEXORIQ is certified with the <strong className="text-nx-navy">ISO 9001:2015 Quality
              Management System</strong>. Our supplier qualification, RFQ handling, inspection
              coordination and documentation workflows all operate under this internationally
              recognised standard — audited, maintained and continuously improved.
            </p>
          </div>
        </div>
      </section>

      {/* Certificate documents */}
      <section className="px-6 md:px-12 lg:px-20 py-24" data-testid="cert-documents">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">
          On record
        </div>
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy max-w-4xl">
          Our certificates.
        </h2>

        {loading ? (
          <div className="mt-12 font-mono text-[0.7rem] uppercase tracking-[0.2em] text-nx-steel2" data-testid="cert-loading">
            → Loading…
          </div>
        ) : certs.length === 0 ? (
          <div className="mt-12 border border-nx-navy/25 p-8 text-nx-navy2 max-w-2xl" data-testid="cert-empty">
            Certificate documents are being updated. For a copy of our ISO 9001:2015 certificate,
            email <a href="mailto:source@nexoriq.co" className="nx-underline text-nx-navy">source@nexoriq.co</a>.
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certs.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ delay: i * 0.06, duration: 0.6 }}
                className="bg-nx-bg border border-nx-navy/25 p-8 flex flex-col"
                data-testid={`cert-card-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setLightbox(c)}
                  className="group relative aspect-[4/3] w-full bg-nx-bg2 border border-nx-line mb-6 flex items-center justify-center overflow-hidden cursor-zoom-in"
                  aria-label={`View ${c.title} full screen`}
                  data-testid={`cert-preview-${i}`}
                >
                  <img
                    src={`${API}/certifications/${c.id}/preview`}
                    alt={c.title}
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="hidden flex-col items-center gap-3 text-nx-steel2">
                    <FileText size={40} strokeWidth={1.4} />
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em]">PDF Document</span>
                  </div>
                  {c.content_type === "application/pdf" && (
                    <span className="absolute top-2 right-2 bg-nx-navy text-nx-bg font-mono text-[0.55rem] uppercase tracking-[0.16em] px-2 py-1">
                      PDF
                    </span>
                  )}
                  <span className="absolute bottom-2 right-2 bg-nx-bg/90 text-nx-navy p-1.5 border border-nx-navy/25 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Maximize2 size={13} />
                  </span>
                </button>
                <h3 className="font-cabinet font-black text-xl tracking-tight text-nx-navy leading-tight">
                  {c.title}
                </h3>
                {c.issuer && (
                  <p className="mt-1.5 text-sm text-nx-navy2">Issued by {c.issuer}</p>
                )}
                <div className="mt-auto pt-6 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setLightbox(c)}
                    className="inline-flex items-center gap-1 border-b border-nx-copper pb-0.5 uppercase tracking-[0.14em] text-[0.65rem] text-nx-copper hover:text-nx-navy transition-colors"
                    data-testid={`cert-view-${i}`}
                  >
                    <Eye size={12} /> View
                  </button>
                  <a
                    href={`${API}/certifications/${c.id}/file?download=true`}
                    className="inline-flex items-center gap-1 border-b border-nx-navy pb-0.5 uppercase tracking-[0.14em] text-[0.65rem] text-nx-navy hover:text-nx-copper transition-colors"
                    data-testid={`cert-download-${i}`}
                  >
                    <Download size={12} /> Download
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-nx-navydark/95 backdrop-blur-sm flex flex-col"
            onClick={() => setLightbox(null)}
            data-testid="cert-lightbox"
          >
            <div className="flex items-center justify-between px-5 md:px-8 py-4 shrink-0">
              <div className="min-w-0 pr-4">
                <div className="font-cabinet font-black text-nx-bg text-base md:text-xl tracking-tight truncate">
                  {lightbox.title}
                </div>
                {lightbox.issuer && (
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel mt-0.5">
                    Issued by {lightbox.issuer}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                {lightbox.content_type === "application/pdf" && (
                  <a
                    href={`${API}/certifications/${lightbox.id}/file`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:inline-flex items-center gap-1.5 border border-nx-bg/40 text-nx-bg hover:border-nx-copper hover:text-nx-copper transition-colors px-3 py-2 font-mono text-[0.62rem] uppercase tracking-[0.16em]"
                    data-testid="cert-lightbox-open-pdf"
                  >
                    <FileText size={12} /> Open PDF
                  </a>
                )}
                <a
                  href={`${API}/certifications/${lightbox.id}/file?download=true`}
                  className="inline-flex items-center gap-1.5 border border-nx-bg/40 text-nx-bg hover:border-nx-copper hover:text-nx-copper transition-colors px-3 py-2 font-mono text-[0.62rem] uppercase tracking-[0.16em]"
                  data-testid="cert-lightbox-download"
                >
                  <Download size={12} /> Download
                </a>
                <button
                  onClick={() => setLightbox(null)}
                  className="text-nx-bg/80 hover:text-nx-bg p-2"
                  aria-label="Close"
                  data-testid="cert-lightbox-close"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
              className="flex-1 min-h-0 px-4 md:px-10 pb-6 overflow-auto flex items-start justify-center"
            >
              <img
                src={`${API}/certifications/${lightbox.id}/preview`}
                alt={lightbox.title}
                className="max-w-full w-auto shadow-2xl bg-white"
                onClick={(e) => e.stopPropagation()}
                data-testid="cert-lightbox-image"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5.4vw] text-nx-bg">
              Source with a certified partner.
            </h2>
            <p className="mt-6 text-nx-bg/75 max-w-xl leading-relaxed">
              Submit your requirement and it will be handled under our ISO 9001:2015 certified
              quality management system — from first brief to final delivery.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/sourcing-request" className="nx-btn on-dark" data-testid="cert-rfq-cta">
              Submit a Requirement <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
